import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { getCompanies, getJobs, getWizardProgress, clearWizardProgress } from '@/lib/dataService';
import { invoicesApi } from '@/services/api';
import { formatRegionMoney, getActiveRegionConfig } from '@/lib/regionConfig';
import { buildChecklist, buildNextSteps, summariseJobs } from '@/lib/dashboardActions';
import StatCards from '@/components/dashboard/StatCards';
import NextStepsPanel from '@/components/dashboard/NextStepsPanel';
import AccountDetailsPanel from '@/components/dashboard/AccountDetailsPanel';
import ChecklistPanel from '@/components/dashboard/ChecklistPanel';
import HelpPanel from '@/components/dashboard/HelpPanel';
import ReferralPanel from '@/components/dashboard/ReferralPanel';
import RecentJobsTable from '@/components/dashboard/RecentJobsTable';
import PendingInvoicesPanel from '@/components/dashboard/PendingInvoicesPanel';
import CompaniesPanel from '@/components/dashboard/CompaniesPanel';

const RECENT_JOB_LIMIT = 5;
const WIZARD_STEP_NAMES = ['Services', 'Companies', 'Review', 'Upload'];
const WIZARD_ROUTES = ['/wizard/start', '/wizard/companies', '/wizard/review', '/wizard/upload'];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const regionConfig = getActiveRegionConfig();
  const [companies, setCompanies] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [savedProgress, setSavedProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [referralCode] = useState(() => {
    let code = localStorage.getItem('ixbrl_referral_code');
    if (!code) {
      code = 'REF-' + Math.random().toString(36).substr(2, 8).toUpperCase();
      localStorage.setItem('ixbrl_referral_code', code);
    }
    return code;
  });

  const displayName = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || '';

  useEffect(() => {
    async function loadData() {
      try {
        const [companiesData, jobsData, wizardData, invoicesData] = await Promise.all([
          getCompanies(),
          getJobs(),
          getWizardProgress(),
          invoicesApi.list().catch((err) => {
            console.error('Failed to load invoices:', err);
            return { invoices: [] };
          }),
        ]);

        const today = new Date().toISOString().slice(0, 10);
        const pending = (invoicesData.invoices || [])
          .filter((inv) => !inv.paid_at)
          .map((inv) => ({
            id: inv.invoice_number,
            company: inv.company_name,
            amount: (inv.total_pence || 0) / 100,
            dueDate: inv.due_date,
            status: inv.due_date && inv.due_date < today ? 'overdue' : 'pending',
          }));

        setCompanies(companiesData);
        setJobs(jobsData);
        setPendingInvoices(pending);
        setSavedProgress(wizardData);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const summary = useMemo(() => summariseJobs(jobs), [jobs]);
  const nextSteps = useMemo(() => buildNextSteps({ jobs, pendingInvoices }), [jobs, pendingInvoices]);
  const checklist = useMemo(() => buildChecklist({ companies, jobs }), [companies, jobs]);
  const recentJobs = useMemo(() => jobs.slice(0, RECENT_JOB_LIMIT), [jobs]);

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Referral code copied to clipboard');
  };

  const continueSavedProgress = () => {
    if (!savedProgress) return;
    sessionStorage.setItem('ixbrl_wizard', JSON.stringify(savedProgress));
    navigate(WIZARD_ROUTES[(savedProgress.step || 1) - 1] || WIZARD_ROUTES[0]);
  };

  const clearSavedProgress = async () => {
    await clearWizardProgress();
    setSavedProgress(null);
    toast.success('Saved progress cleared');
  };

  return (
    <div data-testid="dashboard-page">
      <div className="page-header flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
        <div>
          <h1 className="page-title">Welcome back{displayName ? `, ${displayName}` : ''}</h1>
          <p className="page-subtitle">Manage your iXBRL preparation and filing in one place.</p>
        </div>
        <div className="flex flex-wrap items-center gap-4 lg:ml-auto">
          <StatCards summary={summary} loading={loading} onNavigate={navigate} />
          <Button onClick={() => navigate('/wizard/start')} data-testid="quick-start-btn">
            Create New Job
            <ArrowUpRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {savedProgress && (
        <div className="bg-card border border-border border-l-4 border-l-warning rounded-md p-4 mb-6" data-testid="saved-progress-banner">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Play className="w-5 h-5 shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-sm">{savedProgress.partialSubmitNote || 'You have a saved order in progress'}</p>
                <p className="text-xs text-muted-foreground">
                  {WIZARD_STEP_NAMES[(savedProgress.step || 1) - 1]} (Step {savedProgress.step || 1} of 4)
                  {savedProgress.selectedCompanies?.length > 0 &&
                    ` · ${savedProgress.selectedCompanies.length} ${savedProgress.selectedCompanies.length === 1 ? 'company' : 'companies'}`}
                  {' '}&middot; Saved {new Date(savedProgress.savedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="ghost" size="sm" onClick={clearSavedProgress}>Discard</Button>
              <Button size="sm" onClick={continueSavedProgress}>Continue Order</Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-6 mb-6">
        <div className="space-y-6 min-w-0">
          <NextStepsPanel steps={nextSteps} onNavigate={navigate} />
          <RecentJobsTable jobs={recentJobs} loading={loading} onNavigate={navigate} />
          <CompaniesPanel companies={companies} jobs={jobs} onNavigate={navigate} />
        </div>
        <div className="space-y-6 min-w-0">
          <AccountDetailsPanel name={displayName} email={user?.email} regionConfig={regionConfig} onNavigate={navigate} />
          <ChecklistPanel checklist={checklist} onNavigate={navigate} />
          <PendingInvoicesPanel invoices={pendingInvoices} regionConfig={regionConfig} onNavigate={navigate} />
          <HelpPanel supportEmail={regionConfig.supportEmail} onNavigate={navigate} />
          <ReferralPanel
            rewardText={formatRegionMoney(regionConfig.referralReward, regionConfig)}
            code={referralCode}
            onCopy={copyReferralCode}
            onNavigate={navigate}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
